import { PlusIcon } from '@heroicons/react/24/outline';
import React, { type FC, useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router';

import {
    useCreateContentVariantMutation,
    useCreateExportMutation,
    useCreateFieldValueMutation,
    useCreativeQuery,
    useDeleteContentVariant,
    useFontFileQueries,
    useReconcileCreative,
    useSvgFileQueries,
    useUpdateFieldValueMutation,
} from '@/api/queries';
import type { CreativeDetails, FieldValue } from '@/api/types';
import { BulkIcon } from '@/common/bulk-icon';
import { Button, ButtonSize, ButtonType } from '@/common/button';
import { ConfirmationModal, ConfirmationModalType } from '@/common/confimation-modal';
import { CustomToast } from '@/common/custom-toast';
import { ExportCreativeModal } from '@/common/export-creative-modal';
import { FloatingPanel, FloatingPanelItem, FloatingPanelType } from '@/common/floating-panel';
import { IntaglioIcon } from '@/common/intaglio-icon';
import { ListTile } from '@/common/list-tile';
import { LoadingIcon } from '@/common/loading-icon';
import { Navbar, NavbarType } from '@/common/navbar';
import { SelectSizeModal } from '@/common/select-size-modal';
import { createSession, destroySession, loadFont } from '@/etcher';
import type { SessionId } from '@/etcher/types';
import { debounce, loadFontFile } from '@/utils';

import { DynamicDataTableModal } from './bulk-data-modal';
import { ContentVariantTile } from './content-variant-tile';
import { LoadingScreen } from './loading-screen';
import { ReconcileCreativeModal } from './reconcile-creative-modal';
import { Stage } from './stage';
import { useCreativeGeneratorStore } from './store';

const FIELD_VALUE_DEBOUNCE_TIME = 2000;

export const CreativesGenerator: FC = () => {
    const params = useParams<{ creativeId: string }>();
    const creativeId = Number.parseInt(params.creativeId || '');
    if (Number.isNaN(creativeId)) {
        console.error('Invalid params ID in url params:', params.creativeId);
        return <Navigate to="/creatives" replace={true} />;
    }

    const creative = useCreativeGeneratorStore((s) => s.creative);
    const currentVariantIndex = useCreativeGeneratorStore((s) => s.currentVariantIndex);
    const etcherId = useCreativeGeneratorStore((s) => s.etcherId);
    const selectedContentVariantIndex = useCreativeGeneratorStore((s) => s.selectedContentVariantIndex);
    const setCreative = useCreativeGeneratorStore((s) => s.setCreative);
    const setCurrentVariantIndex = useCreativeGeneratorStore((s) => s.setCurrentVariantIndex);
    const setSelectedContentVariantIndex = useCreativeGeneratorStore((s) => s.setSelectedContentVariantIndex);
    const setEtcherId = useCreativeGeneratorStore((s) => s.setEtcherId);
    const addContentVariant = useCreativeGeneratorStore((s) => s.addContentVariant);
    const removeContentVariant = useCreativeGeneratorStore((s) => s.removeContentVariant);
    const setFieldValue = useCreativeGeneratorStore((s) => s.setFieldValue);
    const removeFieldValue = useCreativeGeneratorStore((s) => s.removeFieldValue);
    const addFontFile = useCreativeGeneratorStore((s) => s.addFontFile);
    const addSvgFile = useCreativeGeneratorStore((s) => s.addSvgFile);
    const clearStore = useCreativeGeneratorStore((s) => s.clear);

    const [initFetchComplete, setInitFetchComplete] = useState(false);
    const [isSelectSizeModalOpen, setSelectSizeModalOpen] = useState(false);
    const [isSwitchToBulkConfirmationModalOpen, setSwitchToBulkConfirmationModalOpen] = useState(false);
    const [isBulkDataModalOpen, setIsBulkDataModalOpen] = useState(false);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [isReconcileConfirmationModalOpen, setIsReconcileConfirmationModalOpen] = useState(false);
    const [dataColumns, setDataColumns] = useState<string[]>(['Col 01', 'Col 02', 'Col 03']);
    const [dataRows, setDataRows] = useState<string[][]>([['Data 01', 'Data 02', 'Data 03']]);
    const [exportCount, setExportCount] = useState<number>(0);

    const navigate = useNavigate();

    const { refetch: refetchCreative } = useCreativeQuery(creativeId, {
        onSuccess: async (creative: CreativeDetails) => {
            setCreative(creative);
            setExportCount((creative?.contentVariants.length || 0) * (creative?.creativeVariants.length || 0));
            setCurrentVariantIndex(0);
            if (creative.contentVariants.length > 0) {
                setSelectedContentVariantIndex(0);
            }
            setIsReconcileConfirmationModalOpen(creative.shouldReconcile);
        },
        staleTime: Number.POSITIVE_INFINITY,
        cacheTime: 0,
    });

    const { mutate: reconcileCreative } = useReconcileCreative();
    const { mutate: createFieldValue, isLoading: isCreateFieldValueLoading } = useCreateFieldValueMutation();
    const { mutate: updateFieldValue, isLoading: isUpdateFieldValueLoading } = useUpdateFieldValueMutation();
    const { mutate: createContentVariant, isLoading: isCreateContentVariantLoading } =
        useCreateContentVariantMutation();
    const { mutate: deleteContentVariant, isLoading: isDeleteConentVariantLoading } = useDeleteContentVariant();
    const { mutate: createExport } = useCreateExportMutation();

    const fonts = creative?.templateVersion.variants.flatMap((v) => v.svgFile.availableFonts) || [];
    const fontFileQueryResults = useFontFileQueries(fonts, { enabled: !!creative });
    const fontFilesLoaded = fontFileQueryResults.every((result) => result.isSuccess);

    const svgFiles = creative?.creativeVariants.map((cv) => cv.variant.svgFile.file) || [];
    const svgFileQueryResults = useSvgFileQueries(svgFiles, { enabled: !!creative });
    const svgFilesLoaded = svgFileQueryResults.every((result) => result.isSuccess);

    const debouncedUpdateFieldValue = useCallback(
        debounce((fieldValue: FieldValue, origFieldValue: FieldValue) => {
            updateFieldValue(
                {
                    id: fieldValue.id as number,
                    value: fieldValue.value,
                },
                {
                    onError: (data) => {
                        setFieldValue(fieldValue.contentVariant, origFieldValue);
                    },
                },
            );
        }, FIELD_VALUE_DEBOUNCE_TIME),
        [],
    );

    const handleContentVariantFieldChange = (
        text: string,
        fieldId: number,
        contentVariantId: number,
        fieldValueId: number | undefined,
    ): void => {
        if (!fieldValueId) {
            createFieldValue(
                {
                    contentVariant: contentVariantId,
                    field: fieldId,
                    value: text,
                },
                {
                    onSuccess: (data) => {
                        setFieldValue(contentVariantId, data);
                    },
                },
            );
        } else {
            const fieldValue = creative?.contentVariants
                .find((cv) => cv.id === contentVariantId)
                ?.fieldValues.find((fv) => fv.id === fieldValueId);
            if (fieldValue) {
                const newFieldValue = Object.assign({}, fieldValue);
                newFieldValue.value = text;
                setFieldValue(contentVariantId, newFieldValue);
                debouncedUpdateFieldValue(newFieldValue, fieldValue);
            } else {
                console.error('Field value not found in content variant:', contentVariantId, fieldValueId);
            }
        }
    };

    const handleDeleteContentVariant = (id: number): void => {
        deleteContentVariant(id, {
            onSuccess: () => {
                removeContentVariant(id);
            },
        });
    };

    const handleAddContentVariant = (): void => {
        createContentVariant(creativeId, {
            onSuccess: (data) => {
                addContentVariant({
                    ...data,
                    fieldValues: [],
                });
                setExportCount((prev) => prev + (creative?.creativeVariants.length || 0));
            },
        });
    };

    const handleExport = (exportFormats: string[]): void => {
        createExport(
            { creative: creativeId, formats: exportFormats },
            {
                onSuccess: () => {
                    CustomToast.success(
                        "Export has been requested. You will receive an email with the download link once it's ready.",
                    );
                },
            },
        );
    };

    const isContentChanging = (): boolean => {
        return (
            isCreateFieldValueLoading ||
            isUpdateFieldValueLoading ||
            isCreateContentVariantLoading ||
            isDeleteConentVariantLoading
        );
    };

    useEffect(() => {
        if (creative && fontFilesLoaded && svgFilesLoaded) {
            const promises = [];
            for (const res of fontFileQueryResults) {
                const { url, file, font } = res.data;
                addFontFile(url, file);
                const loadFontInEtcher = new Promise<void>((resolve, reject) => {
                    if (!etcherId) {
                        console.error('Etcher session not created timely');
                        reject();
                        return;
                    }
                    loadFont(etcherId, file, (loadedFont) => {
                        resolve();
                    });
                });
                promises.push(loadFontFile(font, file), loadFontInEtcher);
            }

            for (const res of svgFileQueryResults) {
                addSvgFile(res.data.url, res.data.file);
            }
            Promise.all(promises).then(() => {
                setInitFetchComplete(true);
            });
        }
    }, [
        addFontFile,
        addSvgFile,
        creative,
        etcherId,
        fontFileQueryResults,
        fontFilesLoaded,
        svgFileQueryResults,
        svgFilesLoaded,
    ]);

    useEffect(() => {
        // Use local variable to prevent biome from complaining and to prevent timing issues if View is
        // unmounted before session has been created
        let etcherId: SessionId | null = null;
        createSession((res) => {
            etcherId = res.sessionId;
            setEtcherId(etcherId);
        });

        return () => {
            if (etcherId) {
                destroySession(etcherId);
            }
            clearStore();
        };
    }, [clearStore, setEtcherId]);

    return (
        <>
            <div className={`flex flex-col min-h-0 h-screen w-full bg-black-50 ${initFetchComplete ? 'hidden' : ''}`}>
                <LoadingScreen />
            </div>
            <div className={`flex flex-col min-h-0 h-screen w-full bg-black-50 ${initFetchComplete ? '' : 'hidden'}`}>
                <Navbar
                    type={NavbarType.LIGHT}
                    leading={
                        <div className="flex flex-row gap-4 items-center">
                            <Link to="/">
                                <IntaglioIcon />
                            </Link>
                            <p className="text-sm text-white font-normal">Produce Creative</p>
                        </div>
                    }
                    center={<p className="text-base text-white">{creative?.name}</p>}
                    trailing={
                        creative && (
                            <Button
                                leading={isContentChanging() && <LoadingIcon className="h-5 w-5 text-white" />}
                                title={`Export ${exportCount} Asset${exportCount === 1 ? '' : 's'}`}
                                buttonType={ButtonType.PRIMARY}
                                buttonSize={ButtonSize.LARGE}
                                disabled={isContentChanging()}
                                onClick={() => {
                                    setIsExportModalOpen(true);
                                }}
                            />
                        )
                    }
                />

                <div className="flex min-h-0 flex-row h-full">
                    <FloatingPanel className="pl-4 py-4 h-full w-[18%] flex-shrink-0" type={FloatingPanelType.LIGHT}>
                        <FloatingPanelItem className="px-4">
                            <div className="flex flex-col gap-1">
                                <p className="text-white text-sm font-normal">Sizes</p>
                            </div>
                        </FloatingPanelItem>
                        <FloatingPanelItem className="px-4">
                            <div className="flex flex-col gap-2">
                                <Button
                                    title="Select Sizes"
                                    className="w-full hover:bg-gray-160"
                                    buttonType={ButtonType.SECONDARY}
                                    buttonSize={ButtonSize.LARGE}
                                    leading={<PlusIcon className="h-5 w-5 text-white" />}
                                    onClick={() => setSelectSizeModalOpen(true)}
                                />
                                <div className="flex flex-col gap-4 overflow-y-auto h-[calc(100vh-300px)] no-scrollbar">
                                    {creative?.creativeVariants.map((cv, index) => (
                                        <ListTile
                                            key={cv.id}
                                            index={index + 1}
                                            title={cv.variant.name}
                                            thumbnail={cv.variant.thumbnail}
                                            size={`${cv.variant.svgFile.width} x ${cv.variant.svgFile.height}`}
                                            isActive={currentVariantIndex === index}
                                            onClick={() => setCurrentVariantIndex(index)}
                                        />
                                    ))}
                                </div>
                            </div>
                        </FloatingPanelItem>
                    </FloatingPanel>

                    <div className="flex-grow flex-shrink h-full min-w-0 overflow-auto">
                        <div className="flex flex-col min-h-full min-w-full justify-center">
                            <Stage />
                        </div>
                    </div>

                    <FloatingPanel
                        className="pr-4 py-4 h-full flex-shrink-0 flex-grow-0"
                        type={FloatingPanelType.LIGHT}
                        width={360}
                    >
                        <FloatingPanelItem className="px-4">
                            <div className="flex flex-row justify-between items-center">
                                <p className="text-sm text-white font-medium">Content</p>
                                {creative && creative.contentVariants.length > 0 && (
                                    <Button
                                        title="Add Content Variant"
                                        className="text-xs hover:bg-gray-160"
                                        buttonType={ButtonType.SECONDARY}
                                        buttonSize={ButtonSize.SMALL}
                                        onClick={handleAddContentVariant}
                                    />
                                )}
                            </div>
                        </FloatingPanelItem>

                        <FloatingPanelItem className="px-4 flex flex-col gap-2 h-full max-h-full min-h-0">
                            {creative?.contentVariants.length === 0 ? (
                                <Button
                                    title="Add a Content Version"
                                    className="w-full"
                                    buttonType={ButtonType.SECONDARY}
                                    buttonSize={ButtonSize.LARGE}
                                    leading={<PlusIcon className="h-5 w-5 text-white" />}
                                    onClick={handleAddContentVariant}
                                />
                            ) : (
                                <div className="flex flex-col flex-grow gap-4 overflow-y-auto no-scrollbar">
                                    {creative?.contentVariants.map((contentVariant, index) => (
                                        <ContentVariantTile
                                            key={contentVariant.id}
                                            index={index}
                                            isSelected={selectedContentVariantIndex === index}
                                            onClick={() => setSelectedContentVariantIndex(index)}
                                            fields={creative?.templateVersion.fields ?? []}
                                            fieldValues={contentVariant.fieldValues}
                                            onChange={(
                                                text: string,
                                                fieldId: number,
                                                fieldValueId: number | undefined,
                                            ) =>
                                                handleContentVariantFieldChange(
                                                    text,
                                                    fieldId,
                                                    contentVariant.id,
                                                    fieldValueId,
                                                )
                                            }
                                            onDelete={() => {
                                                handleDeleteContentVariant(contentVariant.id);
                                            }}
                                        />
                                    ))}
                                </div>
                            )}
                        </FloatingPanelItem>
                    </FloatingPanel>
                </div>

                {creative && (
                    <SelectSizeModal
                        templateId={creative.templateId}
                        isOpen={isSelectSizeModalOpen}
                        setIsOpen={setSelectSizeModalOpen}
                        onDone={() => refetchCreative()}
                        creativeId={creative.id}
                        initiallySelectedSizes={creative.creativeVariants.map((cv) => cv.variant.id)}
                    />
                )}

                <DynamicDataTableModal
                    isOpen={isBulkDataModalOpen}
                    setIsOpen={setIsBulkDataModalOpen}
                    dataColumns={dataColumns}
                    dataRows={dataRows}
                    setDataRows={setDataRows}
                />

                <ConfirmationModal
                    isOpen={isSwitchToBulkConfirmationModalOpen}
                    setIsOpen={setSwitchToBulkConfirmationModalOpen}
                    type={ConfirmationModalType.WARN}
                    confirmText="Yes"
                    cancelText="Cancel"
                    message="Are you sure you want to switch to Bulk Content?"
                    subMessage="All manually entered data will be removed."
                    onConfirm={() => setIsBulkDataModalOpen(true)}
                />

                <ReconcileCreativeModal
                    isOpen={isReconcileConfirmationModalOpen}
                    setIsOpen={setIsReconcileConfirmationModalOpen}
                    onCancel={() => navigate('/creatives')}
                    onConfirm={() => reconcileCreative(creativeId)}
                />

                <ExportCreativeModal
                    isOpen={isExportModalOpen}
                    setIsOpen={setIsExportModalOpen}
                    exportCount={(creative?.contentVariants.length || 0) * (creative?.creativeVariants.length || 0)}
                    onConfirm={handleExport}
                />
            </div>
        </>
    );
};
